import { Component, OnInit, Signal, ViewContainerRef, computed, effect, inject, input, signal, viewChild } from '@angular/core';

import { ComponentRegistry, RegisterComponent } from '../../components/component-lookup';
import { KpiComponent } from '../kpi/kpi.component';
import { MetricService } from '../../services/metric.service';
import { StateService, SubjectType } from '../../services/state.service';
import { VisualService } from '../../services/visual.service';
import { FlexItem } from '../../../shared/services/product.service';

export interface Kpi {
  value?: string;
  label: string;
  tooltip: string;
}

export interface KpiCollectionItem {
  metricId: string;
  data: any;
}

@RegisterComponent('KpiCollection')
@Component({
  selector: 'app-kpi-collection',
  standalone: true,
  imports: [KpiComponent],
  templateUrl: './kpi-collection.component.html',
  styleUrl: './kpi-collection.component.scss'
})
export class KpiCollectionComponent implements OnInit {
  content = viewChild.required('content', { read: ViewContainerRef });
  data = input<any>();
  tourId = input<string>();
  children = input.required<FlexItem[]>();
  subjectType = input.required<SubjectType>();
  type = input<string>('');

  readonly institution = inject(StateService).institution;
  readonly peer = inject(StateService).peer;
  readonly dateRange = inject(StateService).dateRange;
  readonly filteredGroup = inject(StateService).filteredGroup;

  private metricIds = signal<string[]>([]);
  private kpiValues = signal(new Map<string, (number | null)>());

  private metricService = inject(MetricService);
  private visualService = inject(VisualService);
  
  constructor() {
    effect(() => {
      const subjectType = this.subjectType();
      const peer = subjectType == SubjectType.peer ? this.peer() : undefined;
      const date = this.dateRange()?.endDate;
      const metricIds = this.metricIds().join(',');
      const metricCount = this.metricIds().length;
      const group = this.filteredGroup();
      if (subjectType && (peer || subjectType == SubjectType.institution) && date && metricIds.length > 0 && group) {
        setTimeout(() => {
          this.kpiValues.set(this.getEmptyValues());
        });

        this.metricService.getMetricData(this.institution().iid, metricIds, peer).subscribe(metrics => {
          if (metricCount == 1) {
            metrics = [{ metricId: metricIds, data: metrics }];
          }
          const kpis = new Map<string, (number | null)>();
          metrics.forEach((metric: KpiCollectionItem) => {
            let indexes: number[] = [];
            if (subjectType == SubjectType.peer) {
              indexes = this.visualService.getPeerIndexes(metric.data[0]);
            } else {
              indexes.push(metric.data[0].findIndex((c: number) => {
                return c == this.institution().iid;
              }));
            }

            const dates = metric.data.map((row: (string | number | Date)[]) => new Date(row[0]));
            const dataRow = dates.findIndex((d: Date) => new Date(d).getTime() == date.getTime());
            if (dataRow > -1) {
              let sum: number | null = null; let count = 0;
              indexes.forEach(i => {
                const value = metric.data[dataRow][i];
                if (value != null) {
                  sum += value; count++;
                }
              });
              const value: number | null = (sum != null && count > 0) ? sum / count : null;
              kpis.set(metric.metricId, value);
            }
          });
          setTimeout(() => {
            this.kpiValues.set(new Map(kpis));
          });
        });
      }
    });
  }

  ngOnInit(): void {
    const metricIds: string[] = [];

    this.content().clear();
    this.children().forEach(child => {
      metricIds.push(child.data.metricId);
      const component = ComponentRegistry.get(child.name);
      if (component) {
        const componentRef = this.content().createComponent(component);
        if (componentRef) {
          Object.keys(child.data).forEach(key => {
            componentRef.setInput(key, child.data[key]);
          });
          componentRef.setInput('value', this.getKpiValue(child.data.metricId));
          componentRef.setInput('subjectType', this.subjectType());
          componentRef.setInput('type', this.type());
        } else {
          console.error(`Failed to create component: ${child.name}`);
        }
      }
    });
    this.metricIds.set(metricIds);
  }

  getKpiValue(metricId: string): Signal<(number | null)> {
    return computed(() => this.kpiValues().get(metricId) ?? null);
  }

  getEmptyValues(): Map<string, (number | null)> {
    const values = new Map<string, (number | null)>();
    this.children().forEach(child => {
      values.set(child.data.metricId, null);
    });
    return values;
  }
}
